import { GasState } from "@common/constants";
import { hazardOfSky } from "@common/definitions/hazards";
import { LayerContainer } from "@common/utils/layer";
import { mapWeather, type SkyName } from "@common/utils/mapWeather";
import { Numeric } from "@common/utils/math";
import { Vec, type Vector } from "@common/utils/vector";
import { Color, type Container, Filter, GlProgram, Graphics, Rectangle, RenderTexture, RendererType, Sprite, Texture } from "pixi.js";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { CameraManager } from "../managers/cameraManager";
import { GasManager } from "../managers/gasManager";
import { GasMaskView } from "../managers/gasMaskView";
import { LightFans } from "../managers/lightFans";
import { Lighting } from "../managers/lighting";
import { bloomThreshold, isLightingLevel, isShadowLevel, type LightingLevel, type ShadowLevel, sunDirection } from "../managers/lightingLogic";
import { MapManager } from "../managers/mapManager";
import { NORMAL_SCALE, NormalLook } from "../managers/normalLook";
import { UIManager } from "../managers/uiManager";
import { PIXI_SCALE } from "../utils/constants";
import { softwareRendering } from "../utils/graphicsAcceleration";
import { t } from "../utils/translations/phrases";
import { WarAudio } from "./warAudio";
import { GradeFilter } from "./warBloom";
import { WarDecals } from "./warDecals";
import { fxLayerClear } from "./fxLayerLogic";
import { GroundMaterials } from "./groundMaterials";
import { createNoiseTexture, createZoneTexture } from "./warNoise";
import { FX_FRAGMENT, GROUND_FRAGMENT, GROUND_FRAGMENT_MEDIUM, GROUND_NORMAL_FRAGMENT, WAR_VERTEX } from "./warShaders";
import { WarWeather } from "./warWeather";

/**
 * "Battlefield look": replaces the stock clean, bright presentation with a grim WW1 one.
 *  1. colour grade + vignette + drifting fog/smoke (and rain on some maps)
 *  2. the gas becomes a rolling mustard-gas wall, with a gas-mask view inside it and a barrage when it moves
 *  3. the grass-and-grid ground becomes churned mud with craters, flooded shell holes, scorch marks,
 *     puddles, duckboards, barbed wire and fading footprints
 * Quality (Settings → Graphics → Shaders, `cv_war_look`): high (everything), medium (cheaper shaders), low (no shaders:
 * palette, decals, tinted gas). 0.98.6 (the owner: "Shaders has quality (low, medium, high)"): the classic look ("off")
 * is no longer offered; a setting saved as "off" is read as Low. With it the shadows (managers/shadowLook.ts) and the
 * lighting (managers/lighting.ts) have settings of their own, and at High the shaders take the sun from the lighting
 * (the relief lit from its side, the fog glowing toward it) and the grade blooms and shimmers (war/warBloom.ts).
 * 0.98.7 (the owner: "New ground textures", "Lighting that shows each object's shape"): the ground is painted
 * materials (war/groundMaterials.ts, tools/ground/paint_ground.py) lit by the sun through their normal maps, the
 * ground shader has a normal pass for the lighting's picture of the shapes (managers/normalLook.ts), and the grade
 * lights every slope of that picture by the sun and the lights (managers/lightFans.ts).
 */

/** the zone's look for the shaders (0.98.4, definitions/hazards.ts): 0 the gas, 1 dusk's dust storm, 2 the night's wildfire */
const gasLook = (sky: SkyName | undefined): number => {
    switch (hazardOfSky(sky)) {
        case "dust": return 1;
        case "wildfire": return 2;
        default: return 0;
    }
};

export type WarQuality = "off" | "low" | "medium" | "high";

export interface WarWeatherState {
    readonly name: "overcast" | "drizzle" | "rain" | "fog"
    readonly fog: number
    readonly rain: number
    /** 1 under snow (falling flakes, boot prints that stay), 0 otherwise (1.05) */
    readonly snow: number
    /** a thunderstorm: its rain heavier, its wind stronger (1.05) */
    readonly storm: boolean
    readonly wind: Vector
}

/** the grade's colours (see GRADE_FRAGMENT): the battlefield's own for a clear day, each sky's for the others (1.05) */
interface Grade {
    readonly saturation: number
    readonly contrast: number
    readonly gamma: number
    readonly lift: number
    readonly shadow: readonly [number, number, number]
    readonly mid: readonly [number, number, number]
    readonly high: readonly [number, number, number]
    readonly vignette: number
    /** the fog's colour, times its own (white in the snow, warm at dusk, dark in a storm) */
    readonly fog: readonly [number, number, number]
}
const GRADES: Readonly<Record<SkyName, Grade>> = {
    clear: { saturation: 0.8, contrast: 1.05, gamma: 0.96, lift: 0.03, shadow: [0.11, 0.118, 0.07], mid: [0.985, 1.0, 0.9], high: [1.03, 1.0, 0.91], vignette: 0.3, fog: [1, 1, 1] },
    // a low orange sun: warm mids and highlights, plum shadows
    dusk: { saturation: 0.95, contrast: 1.05, gamma: 0.98, lift: 0.03, shadow: [0.12, 0.07, 0.12], mid: [1.08, 0.95, 0.84], high: [1.15, 0.98, 0.8], vignette: 0.42, fog: [1.3, 1.08, 0.95] },
    // under the dark: cool, the blacks kept black, the beams a touch warm
    night: { saturation: 0.72, contrast: 1.04, gamma: 1, lift: 0, shadow: [0.04, 0.05, 0.09], mid: [0.93, 0.97, 1.05], high: [1.03, 1, 0.95], vignette: 0.45, fog: [0.5, 0.55, 0.7] },
    // bright and cold
    snow: { saturation: 0.72, contrast: 1.02, gamma: 0.93, lift: 0.04, shadow: [0.085, 0.1, 0.125], mid: [0.97, 1, 1.04], high: [1, 1.01, 1.04], vignette: 0.24, fog: [1.75, 1.8, 1.9] },
    // dark, grey-blue, washed out
    storm: { saturation: 0.62, contrast: 1.1, gamma: 1.06, lift: 0.025, shadow: [0.07, 0.08, 0.1], mid: [0.9, 0.95, 1.02], high: [0.94, 0.98, 1.05], vignette: 0.46, fog: [0.85, 0.9, 1] }
};

/** the snow's colours for the map and the minimap (1.05), over whatever the look's palette is (0.98.5: the water ice, pale, its banks under snow) */
export const SNOW_PALETTE = {
    grass: "#d5dbe0",
    beach: "#c9ccc9",
    water: "#b3c4d1",
    riverBank: "#bdc6cc",
    trail: "#aba79e",
    border: "#1b2126",
    void: "#0d1013"
} as const;

/** the palette the minimap, the no-shader fallback and the renderer background use */
const PALETTE = {
    grass: "#4b5233",
    beach: "#534d3c",
    water: "#26302a",
    riverBank: "#2b2419",
    trail: "#4a3f31",
    border: "#15140f",
    gas: "hsla(88, 42%, 33%, 0.62)", // minimap and Low preset: green, like the cloud
    void: "#0e0c08"
} as const;

/**
 * How sharp the two shader layers are drawn, in pixels per screen (CSS) pixel. Both are soft (ground materials, fog
 * and gas), so they're drawn small and stretched over the screen: the shader then runs on a fraction of the pixels,
 * whatever the screen's own pixel density. (Pixi runs a filter at the size of whatever it draws into, so a filter's
 * own `resolution` alone doesn't make it cheaper: the layers are drawn into small textures of their own.)
 */
const LAYER_SCALE = {
    high: { ground: 0.75, fx: 0.5 },
    medium: { ground: 0.5, fx: 0.35 }
} as const;

/** how boldly the grade models the shapes' relief (0.98.7), times the sun's own `relief` under each sky */
const SHAPE_RELIEF = 1.1;

/**
 * A steady frame rate below this (frames per second), while playing, lowers the look one step. Kept under 30 so a
 * browser holding the game at 30 to save battery isn't mistaken for a computer that can't keep up.
 */
const SMOOTH_FPS = 27;

const mulberry32 = (seed: number) => (): number => {
    let t = (seed = (seed + 0x6D2B79F5) | 0);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

class WarLookClass {
    quality: WarQuality = "off";
    weather: WarWeatherState = { name: "overcast", fog: 0.22, rain: 0, snow: 0, storm: false, wind: Vec(1, 0.4) };
    /** the match's sky (1.05): its grade and haze */
    private _sky: SkyName = "clear";

    private _root?: Container;
    private _noise?: Texture;
    private _terrain?: RenderTexture;
    private _terrainWide?: RenderTexture;
    private _zone?: Texture;
    /** what's on screen: the two layers' small textures, stretched */
    private _ground?: Sprite;
    private _fx?: Sprite;
    /** the fog's and the gas cloud's layer over the world, when the shaders draw it (the jump-in's sky goes over it: 1.06) */
    get fxLayer(): Sprite | undefined { return this._fx; }
    /** the shaders' full-screen quads, drawn off-screen into those textures every frame */
    private _groundSource?: Sprite;
    private _groundTexture?: RenderTexture;
    private _fxSource?: Sprite;
    private _fxTexture?: RenderTexture;
    private _scales: { readonly ground: number, readonly fx: number } = LAYER_SCALE.high;
    private _groundFilter?: Filter;
    /** the ground's normal pass (0.98.7): its quad, for the lighting's picture of the shapes */
    private _groundNormalFilter?: Filter;
    private _groundNormalSource?: Sprite;
    private _buildingGround?: Graphics;
    private _buildingGroundNormal?: Graphics;
    private _fxFilter?: Filter;
    private _grade?: GradeFilter;
    /** frame times while playing, for lowering the look on a computer that can't keep up */
    private _frameTimes: number[] = [];
    private _frameTotal = 0;
    private _watchFrom = 0;
    private _seed = 0;
    private _time = 0;
    private _mask = 0;
    private _flash = 0;
    private _flashDir = Vec(1, 0);
    private _nextShelling = 0;
    private _lastGasState = GasState.Inactive;

    get active(): boolean { return this.quality !== "off"; }
    /** the shader-based parts (ground, fog, gas cloud, grade) */
    get shaders(): boolean { return this.quality === "high" || this.quality === "medium"; }
    get gasCloudActive(): boolean { return this.shaders && this._fx !== undefined; }

    private _readQuality(): WarQuality {
        const saved = String(GameConsole.getBuiltInCVar("cv_war_look") ?? "high");
        // (0.98.6: three levels; "off", the classic look of before, saved by an older version, is Low now)
        let quality: WarQuality = saved === "low" || saved === "off" ? "low" : saved === "medium" ? "medium" : "high";
        // these shaders are GLSL: anywhere but WebGL keep the look without them. On WebGPU, and on Pixi's Canvas 2D
        // fallback for browsers without WebGL, a GLSL filter is skipped, which left the white sprite under each shader
        // layer on screen: the whole world showed white
        const renderer = Game.pixi.renderer;
        if (quality !== "low" && renderer && renderer.type !== RendererType.WEBGL) quality = "low";
        // drawn without the graphics card (acceleration off in the browser): full-screen shaders run on the processor
        // there, many times slower, so Low for this visit (nothing saved: the chosen look is back with the graphics card)
        if ((quality === "high" || quality === "medium") && softwareRendering()) quality = "low";
        return quality;
    }

    /**
     * Before the renderer starts: will the shader look be drawn? The whole world then goes through a full-screen pass
     * (the grade), which Pixi draws without antialiasing, so the canvas's own antialiasing would only cost time.
     */
    shadersWanted(renderMode: string): boolean {
        const quality = String(GameConsole.getBuiltInCVar("cv_war_look") ?? "high");
        return (quality === "high" || quality === "medium") && renderMode !== "webgpu" && !softwareRendering();
    }

    /** once, when Pixi is ready: the render group that holds the world */
    attach(root: Container): void {
        this._root = root;
    }

    /** before the map is drawn: pick the quality and switch the map colours (minimap included) */
    applyPalette(): void {
        this.quality = this._readQuality();
        MapManager.hideGrid = this.active;
        if (!this.active) return;
        const colors = Game.colors as unknown as Record<string, Color>;
        for (const [key, value] of Object.entries(PALETTE)) colors[key] = new Color(value);
        colors.background = new Color(PALETTE.grass);
        colors.ghillie = new Color(PALETTE.grass);
        Game.pixi.renderer.background.color = colors.background;
        // gas renderers are built before the palette applies, so repaint them in the current gas colour
        MapManager.gasRender?.recolor(Game.colors.gas);
        Game.gasRender?.recolor(Game.colors.gas);
    }

    /** after the map packet: build this map's textures, filters and decals (under the match's sky, 1.05) */
    setupMap(seed: number, sky: SkyName = this._sky): void {
        this._teardown();
        this._seed = seed;
        this._sky = sky;
        this._frameTimes = [];
        this._frameTotal = 0;
        // judge the frame rate only once the match has settled (loading the map and the first frames are slow anyway)
        this._watchFrom = performance.now() + 8000;
        if (!this.active || !this._root) return;

        // the weather is rolled from the seed in shared code (under the match's sky), so the server's fire sees the same rain
        const rolled = mapWeather(seed, sky);
        const wind = Vec(Math.cos(rolled.windAngle) * rolled.windSpeed, Math.sin(rolled.windAngle) * rolled.windSpeed);
        this.weather = { name: rolled.name, fog: rolled.fog, rain: rolled.rain, snow: rolled.snow, storm: sky === "storm", wind };
        // (0.98.6: the clouds' shadows drift with the same wind)
        Lighting.wind = { x: wind.x, y: wind.y };
        // the rest of the look keeps its own sequence (the weather used the first three values)
        const rng = mulberry32(seed ^ 0x5eed);
        rng(); rng(); rng();

        if (this.shaders) this._setupShaders();
        // the Training Range keeps its lanes clean: no shell craters or battlefield decals
        if (Game.isTraining) WarDecals.clear();
        else WarDecals.build(seed, this.quality);
        // (under snow, the battlefield's marks half covered)
        WarDecals.cover(this.weather.snow > 0 ? 0.5 : 0);
        // (the screen's size known first: at Low, without the shaders' layers, nothing had told it, and a sky's snow or
        // rain all fell in the screen's corner, 1.05)
        WarWeather.resize(Game.pixi.screen.width, Game.pixi.screen.height);
        WarWeather.setup(this.quality, this.weather, this._root, this._fx);
        this._nextShelling = 12 + rng() * 20;
        this._lastGasState = GasManager.state;
    }

    private _setupShaders(): void {
        const root = this._root;
        if (!root) return;
        const renderer = Game.pixi.renderer;
        const width = MapManager.width, height = MapManager.height;
        const resolution = renderer.resolution;

        this._noise ??= createNoiseTexture();

        // terrain materials, rasterised once; the wide copy gives soft, wide wet shorelines
        const sharpScale = Math.min(1.5, 2048 / Math.max(width, height));
        const rasterise = (scale: number): RenderTexture => {
            const target = RenderTexture.create({ width: Math.ceil(width * scale), height: Math.ceil(height * scale), resolution: 1, scaleMode: "linear" });
            const ids = new Graphics();
            MapManager.drawTerrainIds(ids, scale);
            renderer.render({ container: ids, target, clear: true, clearColor: [0, 0, 0, 1] });
            ids.destroy();
            return target;
        };
        this._terrain = rasterise(sharpScale);
        this._terrainWide = rasterise(sharpScale / 4);
        this._zone = createZoneTexture(width, height);

        const program = (fragment: string, name: string): GlProgram => GlProgram.from({ vertex: WAR_VERTEX, fragment, name });
        const camera = (): Record<string, { value: unknown, type: string }> => ({
            uCamOrigin: { value: new Float32Array([0, 0]), type: "vec2<f32>" },
            uUnitsPerPx: { value: 1, type: "f32" },
            uTime: { value: 0, type: "f32" }
        });

        const screen = Game.pixi.screen;
        this._scales = this.quality === "high" ? LAYER_SCALE.high : LAYER_SCALE.medium;
        /** a shader layer: its full-screen quad (drawn off-screen into a small texture) and the texture stretched on screen */
        const layer = (filter: Filter, scale: number): { source: Sprite, texture: RenderTexture, shown: Sprite } => {
            const source = new Sprite(Texture.WHITE);
            source.filters = [filter];
            source.width = screen.width; source.height = screen.height;
            const texture = RenderTexture.create({ width: screen.width, height: screen.height, resolution: scale, scaleMode: "linear", dynamic: true });
            const shown = new Sprite(texture);
            shown.width = screen.width; shown.height = screen.height;
            return { source, texture, shown };
        };

        // 1) ground: screen-space layer under the world, shaded in world space (0.98.7: the painted materials, at High
        // lit by the sun through their normals; a second quad draws the ground's normals for the lighting's picture)
        const materials = GroundMaterials.atlas;
        const groundResources = (): Record<string, unknown> => ({
            uNoise: this._noise?.source,
            uTerrain: this._terrain?.source,
            uTerrainWide: this._terrainWide?.source,
            uZone: this._zone?.source,
            uAlbedo: (materials?.albedo ?? Texture.WHITE).source,
            uNormalMap: (materials?.normal ?? Texture.WHITE).source,
            groundUniforms: {
                ...camera(),
                uMapSize: { value: new Float32Array([width, height]), type: "vec2<f32>" },
                uRelief: { value: this.quality === "high" ? 1 : 0, type: "f32" },
                uRain: { value: this.weather.rain, type: "f32" },
                uSnow: { value: this.weather.snow, type: "f32" },
                // (0.98.6: the sun, set each frame from the lighting)
                uSunDir: { value: new Float32Array([-0.7, -0.7]), type: "vec2<f32>" },
                uSunLight: { value: 1, type: "f32" },
                uLightColor: { value: new Float32Array([1, 1, 1]), type: "vec3<f32>" },
                uSunStretch: { value: 1, type: "f32" },
                uMaterials: { value: materials ? 1 : 0, type: "f32" }
            }
        });
        this._groundFilter = new Filter({
            glProgram: program(this.quality === "high" ? GROUND_FRAGMENT : GROUND_FRAGMENT_MEDIUM, this.quality === "high" ? "war-ground" : "war-ground-medium"),
            resources: groundResources(),
            resolution: this._scales.ground
        });
        const ground = layer(this._groundFilter, this._scales.ground);
        this._groundSource = ground.source; this._groundTexture = ground.texture; this._ground = ground.shown;
        root.addChildAt(this._ground, 0);
        // (the normal pass: the same ground, its normals instead of its colours; the lighting draws it into its
        // picture of the shapes at that picture's size: managers/normalLook.ts. At High only: Medium keeps to the
        // colours, its ground's and the shapes', 0.98.7's audit, as the README has it; no normal pass, no picture)
        if (this.quality === "high") {
            this._groundNormalFilter = new Filter({
                glProgram: program(GROUND_NORMAL_FRAGMENT, "war-ground-normals"),
                resources: groundResources(),
                resolution: 1
            });
            const normalSource = new Sprite(Texture.WHITE);
            normalSource.filters = [this._groundNormalFilter];
            normalSource.width = screen.width; normalSource.height = screen.height;
            normalSource.eventMode = "none";
            this._groundNormalSource = normalSource;
            NormalLook.setGround(normalSource);
        }

        // the stock terrain graphics (flat polygons and the grid) give way to the ground shader;
        // buildings' own ground decorations stay
        // (0.98.5's audit: the game shows the terrain again at the first update (`Game.updateLayer`: the basement's
        // rule), so in practice the beach ring, the sea and the rivers have always lain over the shader's ground and
        // only the grass, a hole in them, shows the shader. The rivers and the sea are their own graphics now,
        // hidden here under snow, where the shader draws them as ice, the owner's: the terrain as it was otherwise)
        MapManager.terrainGraphics.visible = false;
        MapManager.riverGraphics.visible = this.weather.snow <= 0;
        this._buildingGround = new Graphics();
        MapManager.drawBuildingGround(this._buildingGround);
        // dirtied down: the bright stock ground patches sit in the mud (under snow, snowed over: their colours are the
        // snow's already, MapManager.groundColor)
        this._buildingGround.tint = this.weather.snow > 0 ? 0xf4f6f8 : 0x9c9180;
        this._buildingGround.zIndex = -5;
        CameraManager.addObject(this._buildingGround);
        // (0.98.7: the patches' normal maps, for the lighting's picture of the shapes: at High, as the normal pass)
        if (this.quality === "high") {
            this._buildingGroundNormal = new Graphics();
            MapManager.drawBuildingGround(this._buildingGroundNormal, true);
            NormalLook.addTwin(this._buildingGroundNormal, () => CameraManager.layerContainers[LayerContainer.Ground]);
        }

        // 2) fog + mustard gas + next-zone ring, at reduced resolution over the world
        this._fxFilter = new Filter({
            glProgram: program(FX_FRAGMENT, "war-fx"),
            resources: {
                uNoise: this._noise.source,
                fxUniforms: {
                    ...camera(),
                    uFog: { value: this.weather.fog, type: "f32" },
                    uFogTint: { value: new Float32Array(GRADES[this._sky].fog), type: "vec3<f32>" },
                    uWind: { value: new Float32Array([this.weather.wind.x, this.weather.wind.y]), type: "vec2<f32>" },
                    uPlayer: { value: new Float32Array([0, 0]), type: "vec2<f32>" },
                    uGasCenter: { value: new Float32Array([0, 0]), type: "vec2<f32>" },
                    uGasRadius: { value: 1e5, type: "f32" },
                    uGasOn: { value: 0, type: "f32" },
                    uAdvancing: { value: 0, type: "f32" },
                    uGasLook: { value: gasLook(this._sky), type: "f32" },
                    uNextCenter: { value: new Float32Array([0, 0]), type: "vec2<f32>" },
                    uNextRadius: { value: 0, type: "f32" },
                    uNextOn: { value: 0, type: "f32" },
                    // screen pixels per pixel of this layer: keeps the thin next-zone line visible at its reduced size
                    uPxSize: { value: 1 / this._scales.fx, type: "f32" },
                    // (0.98.6: the sun, set each frame from the lighting: the fog glows toward it)
                    uSunDir: { value: new Float32Array([-0.7, -0.7]), type: "vec2<f32>" },
                    uSunGlow: { value: 0, type: "f32" },
                    uLightColor: { value: new Float32Array([1, 1, 1]), type: "vec3<f32>" },
                    uScreen: { value: new Float32Array([1, 1]), type: "vec2<f32>" }
                }
            },
            resolution: this._scales.fx
        });
        const fx = layer(this._fxFilter, this._scales.fx);
        this._fxSource = fx.source; this._fxTexture = fx.texture; this._fx = fx.shown;
        root.addChildAt(this._fx, root.getChildIndex(CameraManager.container) + 1);

        // 3) the grade over everything in the world group (the minimap and HUD are elsewhere), the sky's (1.05); with the
        // bloom's passes and the heat haze (0.98.6, the lighting at High: war/warBloom.ts)
        const grade = GRADES[this._sky];
        this._grade = new GradeFilter({
            uScreen: { value: new Float32Array([1, 1]), type: "vec2<f32>" },
            uTime: { value: 0, type: "f32" },
            uSaturation: { value: grade.saturation, type: "f32" },
            uContrast: { value: grade.contrast, type: "f32" },
            uGamma: { value: grade.gamma, type: "f32" },
            uLift: { value: grade.lift, type: "f32" },
            uShadowTint: { value: new Float32Array(grade.shadow), type: "vec3<f32>" },
            uMidTint: { value: new Float32Array(grade.mid), type: "vec3<f32>" },
            uHighTint: { value: new Float32Array(grade.high), type: "vec3<f32>" },
            uVignette: { value: grade.vignette, type: "f32" },
            uMask: { value: 0, type: "f32" },
            uGasTint: { value: 0, type: "f32" },
            uGasLook: { value: gasLook(this._sky), type: "f32" },
            uFlash: { value: 0, type: "f32" },
            uFlashDir: { value: new Float32Array([1, 0]), type: "vec2<f32>" }
        }, resolution);
        root.filters = [this._grade];
        this.resize();
    }

    private _teardown(): void {
        const root = this._root;
        if (root && this._grade) root.filters = [];
        this._ground?.destroy(); this._ground = undefined;
        this._fx?.destroy(); this._fx = undefined;
        this._groundSource?.destroy(); this._groundSource = undefined;
        this._fxSource?.destroy(); this._fxSource = undefined;
        this._groundTexture?.destroy(true); this._groundTexture = undefined;
        this._fxTexture?.destroy(true); this._fxTexture = undefined;
        this._buildingGround?.destroy(); this._buildingGround = undefined;
        if (this._buildingGroundNormal) NormalLook.removeTwin(this._buildingGroundNormal);
        this._buildingGroundNormal?.destroy(); this._buildingGroundNormal = undefined;
        this._groundFilter?.destroy(); this._groundFilter = undefined;
        NormalLook.setGround(undefined);
        this._groundNormalSource?.destroy(); this._groundNormalSource = undefined;
        this._groundNormalFilter?.destroy(); this._groundNormalFilter = undefined;
        this._fxFilter?.destroy(); this._fxFilter = undefined;
        this._grade?.destroy(); this._grade = undefined;
        this._terrain?.destroy(true); this._terrain = undefined;
        this._terrainWide?.destroy(true); this._terrainWide = undefined;
        this._zone?.destroy(true); this._zone = undefined;
        MapManager.terrainGraphics.visible = true;
        MapManager.riverGraphics.visible = true;
        WarDecals.clear();
        WarWeather.clear();
        WarAudio.setBreathing(false);
        this._mask = 0; this._flash = 0;
    }

    /** leaving a match */
    reset(): void {
        WarAudio.setBreathing(false);
        this._mask = 0; this._flash = 0;
        WarWeather.clearTransient();
    }

    resize(): void {
        const { width, height } = Game.pixi.screen;
        const layers = [[this._groundSource, this._groundTexture, this._ground, this._scales.ground], [this._fxSource, this._fxTexture, this._fx, this._scales.fx]] as const;
        for (const [source, texture, shown, scale] of layers) {
            if (!source || !texture || !shown) continue;
            source.width = width; source.height = height;
            texture.resize(width, height, scale);
            shown.width = width; shown.height = height;
        }
        if (this._groundNormalSource) { this._groundNormalSource.width = width; this._groundNormalSource.height = height; }
        if (this._root && this._grade) {
            this._root.filterArea = new Rectangle(0, 0, width, height);
            const screen = this._grade.uniforms.uScreen as Float32Array;
            screen[0] = width; screen[1] = height;
        }
        if (this._fxFilter) {
            const screen = this._fxFilter.resources.fxUniforms.uniforms.uScreen as Float32Array;
            screen[0] = width; screen[1] = height;
        }
        WarWeather.resize(width, height);
    }

    /**
     * whether every pixel of the fog's layer comes out empty this frame (0.98.8: war/fxLayerLogic.ts), from the
     * uniforms just set, over the screen from (`originX`, `originY`), `unitsPerPx` game units a pixel
     */
    private _fxClear(originX: number, originY: number, unitsPerPx: number): boolean {
        const filter = this._fxFilter;
        if (!filter) return false;
        const u = filter.resources.fxUniforms.uniforms;
        const gas = u.uGasCenter as Float32Array, next = u.uNextCenter as Float32Array;
        const { width, height } = Game.pixi.screen;
        return fxLayerClear({
            fog: u.uFog as number,
            gasOn: u.uGasOn as number, gasX: gas[0], gasY: gas[1], gasRadius: u.uGasRadius as number,
            nextOn: u.uNextOn as number, nextX: next[0], nextY: next[1], nextRadius: u.uNextRadius as number,
            pxSize: u.uPxSize as number
        }, originX, originY, originX + width * unitsPerPx, originY + height * unitsPerPx, unitsPerPx);
    }

    /** the gas circle as drawn this frame (same interpolation as GasRender) */
    private _gasNow(): { position: Vector, radius: number } {
        if (GasManager.state === GasState.Advancing) {
            const t = Numeric.clamp((Date.now() - GasManager.lastUpdateTime) / Game.serverDt, 0, 1);
            return { position: Vec.lerp(GasManager.lastPosition, GasManager.position, t), radius: Numeric.lerp(GasManager.lastRadius, GasManager.radius, t) };
        }
        return { position: GasManager.position, radius: GasManager.radius };
    }

    /**
     * A computer that can't keep up gets the next lighter look by itself (High, then Medium, then Low): the frame rate
     * is measured over a few seconds of play (never in the menu, a background tab or the first seconds of a match),
     * and the new level is remembered (Settings → Graphics shows it, and it can be put back there).
     */
    private _watchFrameRate(deltaMS: number): void {
        if (!this.shaders || !Game.gameStarted || Game.gameOver || document.hidden || !GameConsole.getBuiltInCVar("cv_auto_quality")) {
            this._frameTimes.length = 0;
            this._frameTotal = 0;
            return;
        }
        if (performance.now() < this._watchFrom || deltaMS > 250) return; // settling in, or a hitch: not the steady rate
        this._frameTimes.push(deltaMS);
        this._frameTotal += deltaMS;
        if (this._frameTotal < 4000) return;
        // four seconds of frames: their median is the steady frame time
        const sorted = this._frameTimes.sort((a, b) => a - b);
        const median = sorted[sorted.length >> 1];
        this._frameTimes = [];
        this._frameTotal = 0;
        if (median <= 1000 / SMOOTH_FPS) return;

        const next: WarQuality = this.quality === "high" ? "medium" : "low";
        this._lower(next, next === "medium" ? t("Graphics lowered to Medium to keep the game smooth") : t("Graphics lowered to Low to keep the game smooth"));
    }

    /**
     * The world kept failing to draw (see utils/frameGuard.ts): the shaders are the likeliest thing a graphics card or
     * browser can't run, so drop to Low, which has none (remembered, like the slow-computer step down). False when the
     * look has no shaders already: then something else is failing.
     */
    dropShadersAfterError(): boolean {
        if (!this.shaders) return false;
        this._lower("low", t("Graphics lowered to Low: this computer couldn't draw the battlefield's effects"));
        return true;
    }

    private _lower(next: WarQuality, message: string): void {
        GameConsole.setBuiltInCVar("cv_war_look", next);
        const select = document.getElementById("war-look-select") as HTMLSelectElement | null;
        if (select) select.value = next;
        // (0.98.6: the shadows and the lighting a step down with it: they cost their share, and they apply at once)
        const shadows = GameConsole.getBuiltInCVar("cv_shadows");
        const lighting = GameConsole.getBuiltInCVar("cv_lighting");
        const lowered = <T extends ShadowLevel | LightingLevel>(level: T): T => (level === "high" ? "low" : "off") as T;
        if (isShadowLevel(shadows) && shadows !== "off") GameConsole.setBuiltInCVar("cv_shadows", lowered(shadows));
        if (isLightingLevel(lighting) && lighting !== "off") GameConsole.setBuiltInCVar("cv_lighting", lowered(lighting));
        for (const [id, name] of [["shadows-select", "cv_shadows"], ["lighting-select", "cv_lighting"]] as const) {
            const element = document.getElementById(id) as HTMLSelectElement | null;
            if (element) element.value = String(GameConsole.getBuiltInCVar(name));
        }
        this.applyPalette();
        this.setupMap(this._seed);
        const { inventoryMsg } = UIManager.ui;
        inventoryMsg.text(message).fadeOut(0).fadeIn(250);
        clearTimeout(Game.inventoryMsgTimeout);
        Game.inventoryMsgTimeout = window.setTimeout(() => inventoryMsg.fadeOut(250), 5000);
    }

    /** this frame's clock advanced (`prepare`), not yet taken by `update` */
    private _prepared = false;

    /**
     * every frame, after the camera and the lighting and before the shapes' picture (0.98.8): the frame's clock, and
     * the ground's normal pass's camera and sun. The shapes' picture (managers/normalLook.ts) draws that pass before
     * `update` runs, and it took the camera `update` had set the frame before: the ground's relief was a frame behind
     * the ground as the camera moved
     */
    prepare(deltaMS: number): void {
        if (!this.active || this._prepared) return;
        this._prepared = true;
        this._watchFrameRate(deltaMS);
        this._time += Math.min(deltaMS, 100) / 1000;
        const filter = this._groundNormalFilter;
        if (!filter) return;
        const scale = CameraManager.container.scale.x;
        const unitsPerPx = 1 / (scale * PIXI_SCALE);
        this._groundUniforms(filter, unitsPerPx / NORMAL_SCALE, -CameraManager.container.position.x * unitsPerPx, -CameraManager.container.position.y * unitsPerPx);
    }

    /** a ground pass's camera, clock and sun for the frame */
    private _groundUniforms(filter: Filter, perPx: number, originX: number, originY: number): void {
        const sun = Lighting.sun;
        const toward = sunDirection(sun);
        const u = filter.resources.groundUniforms.uniforms;
        (u.uCamOrigin as Float32Array)[0] = originX; (u.uCamOrigin as Float32Array)[1] = originY;
        // (the normal pass is drawn at the lighting's picture's size: its pixels are bigger)
        u.uUnitsPerPx = perPx; u.uTime = this._time;
        (u.uSunDir as Float32Array)[0] = -toward.x; (u.uSunDir as Float32Array)[1] = -toward.y;
        // (0.98.7's audit: the ground's slopes lit once, by the grade from the normals' picture when it's drawn,
        // the ground's own normal pass in it; by the ground shader when it isn't. The wet sheen is the shader's)
        u.uSunLight = filter === this._groundFilter && NormalLook.texture ? 0 : sun.relief;
        u.uSunStretch = sun.stretch;
        const light = u.uLightColor as Float32Array;
        light[0] = sun.light[0]; light[1] = sun.light[1]; light[2] = sun.light[2];
    }

    update(deltaMS: number): void {
        if (!this.active) return;
        // (the clock advanced before the shapes' picture: `prepare`; here if nothing did)
        this.prepare(deltaMS);
        this._prepared = false;
        const dt = Math.min(deltaMS, 100) / 1000;

        const scale = CameraManager.container.scale.x;
        const unitsPerPx = 1 / (scale * PIXI_SCALE);
        const originX = -CameraManager.container.position.x * unitsPerPx;
        const originY = -CameraManager.container.position.y * unitsPerPx;
        const player = Game.activePlayer;
        const gas = this._gasNow();
        const gasOn = GasManager.state !== GasState.Inactive && gas.radius < 5000;

        // gas state changes: the barrage when it starts to move
        if (GasManager.state !== this._lastGasState) {
            if (GasManager.state === GasState.Advancing) WarAudio.barrage();
            this._lastGasState = GasManager.state;
        }

        // inside the gas: the mask comes down
        const inGas = (gasOn && player !== undefined && !player.dead && Vec.len(Vec.sub(player.position, gas.position)) > gas.radius) || GasMaskView.inGrenadeGas;
        const masked = player?.equipment.mask !== undefined;
        this._mask = Numeric.clamp(this._mask + (inGas && !masked ? dt / 0.6 : -dt / 0.45), 0, 1);
        WarAudio.setBreathing(inGas && Game.gameStarted && !Game.gameOver);

        // distant shelling every now and then: a rumble and a glow on the horizon
        this._nextShelling -= dt;
        if (this._nextShelling <= 0 && Game.gameStarted && !Game.gameOver) {
            this._nextShelling = 22 + Math.random() * 40;
            const angle = Math.random() * Math.PI * 2;
            this._flashDir = Vec(Math.cos(angle), Math.sin(angle));
            this._flash = 1;
            WarAudio.distantShelling(this._flashDir.x);
        }
        this._flash = Math.max(0, this._flash - dt / 1.1);
        const flicker = this._flash > 0 ? this._flash * (0.65 + 0.35 * Math.sin(this._time * 38)) * 0.085 : 0;

        // (the ground atlas's mips clamped on the textures the card has now: war/groundMaterials.ts, 0.98.7's audit)
        if (this._groundFilter) GroundMaterials.clampMips();

        // the sun (0.98.6, managers/lighting.ts): toward the light is the other way from the shadows
        const sun = Lighting.sun;
        const toward = sunDirection(sun);
        // (the colour pass's here, after the shapes' picture: it lights its own slopes only without one; the normal
        // pass's were set before that picture drew it: `prepare`)
        if (this._groundFilter) this._groundUniforms(this._groundFilter, unitsPerPx, originX, originY);
        if (this._fxFilter) {
            const u = this._fxFilter.resources.fxUniforms.uniforms;
            (u.uCamOrigin as Float32Array)[0] = originX; (u.uCamOrigin as Float32Array)[1] = originY;
            u.uUnitsPerPx = unitsPerPx; u.uTime = this._time;
            if (player) { (u.uPlayer as Float32Array)[0] = player.position.x; (u.uPlayer as Float32Array)[1] = player.position.y; }
            (u.uGasCenter as Float32Array)[0] = gas.position.x; (u.uGasCenter as Float32Array)[1] = gas.position.y;
            u.uGasRadius = gas.radius;
            u.uGasOn = gasOn ? 1 : 0;
            u.uAdvancing = Numeric.lerp(u.uAdvancing as number, GasManager.state === GasState.Advancing ? 1 : 0, Math.min(1, dt * 2));
            const nextOn = GasManager.state === GasState.Waiting && GasManager.newRadius < gas.radius - 0.5;
            (u.uNextCenter as Float32Array)[0] = GasManager.newPosition.x; (u.uNextCenter as Float32Array)[1] = GasManager.newPosition.y;
            u.uNextRadius = GasManager.newRadius;
            u.uNextOn = nextOn ? 1 : 0;
            (u.uSunDir as Float32Array)[0] = -toward.x; (u.uSunDir as Float32Array)[1] = -toward.y;
            // (the fog glows toward the sun: most at dusk, a little by day, none under a storm's grey or the night)
            u.uSunGlow = Lighting.active ? (this._sky === "dusk" ? 0.8 : this._sky === "clear" ? 0.25 : this._sky === "snow" ? 0.2 : 0) : 0;
            const light = u.uLightColor as Float32Array;
            light[0] = sun.light[0]; light[1] = sun.light[1]; light[2] = sun.light[2];
        }
        if (this._grade) {
            const u = this._grade.uniforms;
            u.uTime = this._time;
            u.uMask = this._mask * this._mask * (3 - 2 * this._mask);
            u.uGasTint = this._mask * 0.4;
            u.uFlash = flicker;
            (u.uFlashDir as Float32Array)[0] = this._flashDir.x; (u.uFlashDir as Float32Array)[1] = this._flashDir.y;
            // (0.98.6: the bloom and the heat haze, the lighting at High)
            this._grade.bloom = Lighting.rich;
            this._grade.heat = Lighting.rich ? Lighting.heat : undefined;
            this._grade.threshold = bloomThreshold(this._sky);
        }
        // the two shader layers, into their small textures (the frame itself is drawn right after this, with them in it)
        const renderer = Game.pixi.renderer;
        if (this._groundSource && this._groundTexture) renderer.render({ container: this._groundSource, target: this._groundTexture, clear: true });
        // (0.98.8: the fog's layer, when every pixel of it would come out empty, neither drawn nor laid over the frame:
        // laid over it, an empty layer changed nothing)
        const fxClear = this._fxClear(originX, originY, unitsPerPx);
        if (this._fx && this._fx.renderable === fxClear) this._fx.renderable = !fxClear;
        if (this._fxSource && this._fxTexture && !fxClear) renderer.render({ container: this._fxSource, target: this._fxTexture, clear: true });
        // the stock flat gas is replaced by the cloud (the low preset keeps it, recoloured)
        Game.gasRender.graphics.visible = !this.gasCloudActive;

        WarDecals.update(originX, originY, Game.pixi.screen.width * unitsPerPx, Game.pixi.screen.height * unitsPerPx);
        WarWeather.update(dt, originX, originY, unitsPerPx);
    }

    /**
     * after the last light is drawn (game.ts, after the night's): the grade takes the shapes' relief for the frame
     * (0.98.7): the normals' picture, the lights' relief, the sun's slope and strength
     */
    finishLighting(): void {
        if (!this._grade) return;
        const sun = Lighting.sun;
        const toward = sunDirection(sun);
        this._grade.setRelief(NormalLook.texture, LightFans.relief, -toward.x, -toward.y, sun.stretch, SHAPE_RELIEF * sun.relief, sun.light);
    }
}

export const WarLook = new WarLookClass();
